import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	closestOptional,
	formatPhoneNumber,
	getAttributeOrThrow,
	getOrThrow,
	getOrUndefined,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { WaitlistPopup, type WelcomePopup } from '@/scripts/interactive-defer/welcome-popup'

export class KlaviyoForm extends UcoastEl {
	static htmlSelector = 'klaviyo-form'
	form: HTMLFormElement
	submitButton: HTMLButtonElement
	successEl: HTMLElement
	emailInput: HTMLInputElement
	originalSubmitCopy: string
	successMessage: string
	route: string
	popup?: WelcomePopup
	termsElements?: {
		termsAccepted: HTMLButtonElement
		termsLabel: HTMLLabelElement
	}
	termsAccepted = true
	companyId: string
	isWaitlist: boolean

	constructor() {
		super()
		this.isWaitlist = this.hasAttribute('data-waitlist')
		this.successEl = qsRequired('[data-success-el]', this)
		this.originalSubmitCopy = this.successEl.textContent ?? ''
		this.form = qsRequired('form', this)
		this.submitButton = qsRequired('[type="submit"]', this)
		this.emailInput = qsRequired('[type="email"]', this)
		this.successMessage = this.getSuccessMessage()
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
		this.popup = this.isWaitlist ? closestOptional<WaitlistPopup>(this, 'waitlist-popup') : closestOptional<WelcomePopup>(this, 'welcome-popup')
		this.termsElements = this.getTermsElements()
		this.termsSetup()
		this.companyId = getAttributeOrThrow('data-company-id', this)
		this.route = this.isWaitlist
			? `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.companyId}`
			: `https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`
	}

	getTermsElements() {
		const termsAccepted = qsOptional<HTMLButtonElement>(
			'button[data-terms-accepted]',
			this
		)
		const termsLabel = qsOptional<HTMLLabelElement>(
			'[data-terms-label]',
			this
		)
		if (termsAccepted && termsLabel) {
			return {
				termsAccepted,
				termsLabel,
			}
		}
		return undefined
	}

	termsSetup() {
		if (!this.termsElements) return
		this.termsAccepted = false
		this.termsElements.termsAccepted.addEventListener(
			'click',
			this.toggleTerms.bind(this)
		)
		this.termsElements.termsLabel.addEventListener(
			'click',
			this.toggleTerms.bind(this)
		)
	}

	toggleTerms() {
		if (!this.termsElements) throw new Error('Terms elements not found')
		if (this.termsAccepted) {
			this.termsAccepted = false
			this.termsElements.termsAccepted.classList.remove('active')
			this.submitButton.setAttribute('disabled', '')
		} else {
			this.termsElements.termsAccepted.classList.add('active')
			this.submitButton.removeAttribute('disabled')
			this.termsAccepted = true
		}
	}

	getSuccessMessage(): string {
		const message = this.form.getAttribute('data-success')
		return message ?? 'Thank You'
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		if (this.emailInput.value.length < 1) return

		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.classList.add('loading')

		const formData = new FormData(this.form)

		const config = this.isWaitlist
			? this.addToWaitlistConfig(formData)
			: this.addToKlaviyoListConfig(formData)

		fetch(this.route, config)
			.then((response) => response.text())
			.then((response) => {

				if (response?.length) {
					this.renderError()
					return
				}
				this.renderSuccess()
			})
			.catch((e) => {
				this.renderError()
				console.error(e)
			})
	}

	renderError() {
		this.submitButton.classList.remove('loading')
		this.submitButton.removeAttribute('aria-disabled')
		this.successEl.innerHTML = 'Invalid email or phone number.'
		window.setTimeout(() => {
			this.successEl.innerHTML = this.originalSubmitCopy
		}, 1200)
	}

	renderSuccess() {
		this.submitButton.classList.remove('loading')
		this.successEl.classList.remove('hidden')
		this.successEl.innerHTML = this.successMessage?.length ? this.successMessage : 'Added to Waitlist'
		if (this.popup) {
			window.setTimeout(() => {
				this.popup?.hide()
			}, 2500)
		}
	}

	addToKlaviyoListConfig(body: FormData) {
		const values = {
			email: getOrThrow(body, 'email'),
			list_id: getOrThrow(body, 'list_id'),
			custom_source: getOrThrow(body, 'custom_source'),
			phone_number: getOrUndefined(body, 'phone_number'),
			language_iso: getOrUndefined(body, 'language_iso') ?? 'en',
			market_handle: getOrUndefined(body, 'market_handle') ?? 'us',
		}
		if (values.phone_number) {
			values.phone_number = formatPhoneNumber(values.phone_number)
		}
		return {
			method: 'POST',
			mode: 'cors' as RequestMode,
			headers: {
				'Content-Type': 'application/json',
				Revision: '2024-05-15',
			},
			body: JSON.stringify({
				data: {
					type: 'subscription',
					attributes: {
						custom_source: values.custom_source,
						profile: {
							data: {
								type: 'profile',
								attributes: {
									email: values.email,
									phone_number: values.phone_number,
									properties: {
										language_iso: values.language_iso,
										market_handle: values.market_handle,
									},
								},
							},
						},
					},
					relationships: {
						list: {
							data: {
								type: 'list',
								id: values.list_id,
							},
						},
					},
				},
			}),
		}
	}

	addToWaitlistConfig(body: FormData) {
		const values = {
			email: getOrThrow(body, 'email'),
			variant_id: getOrThrow(body, 'variant_id'),
			custom_source: getOrThrow(body, 'custom_source'),
			phone_number: getOrUndefined(body, 'phone_number'),
			language_iso: getOrUndefined(body, 'language_iso') ?? 'en',
			market_handle: getOrUndefined(body, 'market_handle') ?? 'us',
		}
		if (values.phone_number) {
			values.phone_number = formatPhoneNumber(values.phone_number)
		}
		return {
			method: 'POST',
			headers: {
				accept: 'application/json',
				Revision: '2024-05-15',
				'content-type': 'application/json',
			},
			body: JSON.stringify({
				data: {
					type: 'back-in-stock-subscription',
					attributes: {
						channels: ['EMAIL', 'SMS'],
						profile: {
							data: {
								type: 'profile',
								attributes: {
									email: values.email,
									phone_number: values.phone_number
								},
							},
						},
					},
					relationships: {
						variant: {
							data: {
								type: 'catalog-variant',
								id: `$shopify:::$default:::${values.variant_id}`,
							},
						},
					},
				},
			}),
		}
	}
}
